"use client";
import * as React from "react";
import { Inbox, ChevronRight, PanelLeft, Files } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import Logo from "@/shared/components/logo/logo";
import { getDashboardLink, getDashboardRoute } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";
import { useSelector, useDispatch } from "@/core/redux/store";
import { NavWorkspace } from "@/shared/components/ui/nav-workspace";
import {
  DashboardIcon,
  NewSpecificationIcon,
  SupplierResponsesIcon,
  TemplatesIcon,
} from "./dashboard-icons/dashboard-icons";
import { useState } from "react";
import FolderSelectionModal from "@/shared/components/modals/folder-selection-modal";
import { createSpecificationThunk } from "@/core/redux/thunks/specification-folders-thunk";

export interface SidebarItem {
  title: string;
  url: string;
  icon?: React.ElementType;
  badge?: number;
  items?: SidebarItem[];
  isActiveCb?: (currentPathname: string) => boolean;
  onClick?: () => void;
}

export function SimpleDashboardSidebar({
  skeleton,
  className,
  hideWorkspaces,
  items: propItems,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  skeleton?: boolean;
  hideWorkspaces?: boolean;
  items?: SidebarItem[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const me = useSelector((state) => state.user.me);
  const unreadInboxCount = useSelector((state) => state.user.unreadInboxCount);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);

  // Define the menu items structure
  // Exported for testing types if needed, but locally here is fine if we use any in tests or expect users to conform
  // Ideally move interface export to top of file

  const defaultItems: SidebarItem[] = [
    {
      title: t("dashboard"),
      url: getDashboardRoute(),
      icon: DashboardIcon,
      isActiveCb: (currentPathname: string) =>
        currentPathname === getDashboardRoute(),
    },
    {
      title: t("inbox:inbox"),
      url: getDashboardLink(ROUTES.Dashboard.Inbox.Root),
      icon: Inbox,
      badge: unreadInboxCount,
    },
    {
      title: t("specification"),
      url: "#",
      // Add icon to default items if desired, or keep as is.
      // Keeping as is to not change UI visually unless requested.
      items: [
        {
          title: t("specification:my-specs"),
          url: getDashboardLink(ROUTES.Dashboard.MySpecs.Root),
          icon: () => <Files width={16} />,
        },
        {
          title: t("new-specification") || "Yeni şartname",
          url: "#",
          icon: NewSpecificationIcon,
          onClick: () => setIsFolderModalOpen(true),
        },
        {
          title: t("templates") || "Şablonlar",
          url: getDashboardLink(ROUTES.Dashboard.Specification.Templates),
          icon: TemplatesIcon,
        },
        {
          title: t("supplier-responses") || "Tedarikçi yanıtları",
          url: getDashboardLink(
            ROUTES.Dashboard.Specification.SupplierResponsesAll
          ),
          icon: SupplierResponsesIcon,
        },
      ],
    },
  ];

  const items = propItems || defaultItems;

  const isActive = (url: string) => {
    if (url === "#") return false;
    return location.pathname === url;
  };

  const handleFolderSelect = async (folderId: string | null, specificationName?: string) => {
    const company = me?.company;
    if (!company) return;

    try {
      const result = await dispatch(
        createSpecificationThunk({
          companyId: company.id,
          folderId: folderId || undefined,
          name: specificationName || undefined,
        })
      );

      const payload = (result as {
        payload?: {
          success?: boolean;
          data?: {
            id?: string;
            specification?: {
              id?: string;
            };
          };
        };
      }).payload;

      if (!payload?.success) return;

      const createdSpecificationId =
        payload.data?.specification?.id || payload.data?.id;

      if (createdSpecificationId) {
        navigate(
          `${getDashboardLink(
            ROUTES.Dashboard.Specification.Create
          )}?specificationId=${createdSpecificationId}`
        );
      }
    } catch (error) {
      console.error("Specification oluşturulamadı:", error);
    }
  };

  const menuItems = items || defaultItems;

  return (
    <div
      className={`flex flex-col fixed top-0 left-0 bottom-0 w-[240px] h-screen bg-[#FAFAFA]  ${className || ""
        }`}
      {...props}
    >
      {/* Header */}
      <div className="px-[8px] py-4 flex items-center justify-between">
        <div className="px-2.5 py-2">
          <a href={getDashboardLink("")}>
            <Logo style={{ height: "22px" }} />
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col px-[8px] overflow-y-auto">
        <nav className="flex flex-col gap-1">
          {menuItems.map((item) => {
            const isCollapsible = item.items && item.items.length > 0;
            const active = item.isActiveCb
              ? item.isActiveCb(location.pathname)
              : isActive(item.url);

            if (isCollapsible) {
              return (
                <Collapsible
                  key={item.title}
                  defaultOpen={true}
                  className="group/collapsible"
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center cursor-pointer gap-[2px] px-2 mt-[8px] text-[#3F3F46] font-600 rounded-md">
                      {item.icon ? (
                        <div className="flex items-center gap-2.5">
                          <item.icon className="w-4 h-4 text-[#3F3F46]" />
                          <span className="text-[#3F3F46B3] text-[12px] font-600">
                            {item.title}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2.5">
                          <span className="text-[#3F3F46B3] text-[12px] font-600">
                            {item.title}
                          </span>
                        </div>
                      )}
                      <ChevronRight
                        className=" text-zinc-400 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                        width={12}
                        height={12}
                      />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col gap-[2px] mt-1">
                      {item.items!.map((subItem) => {
                        const subActive = isActive(subItem.url);
                        return (
                          <div
                            key={subItem.title}
                            onClick={() => subItem.onClick ? subItem.onClick() : navigate(subItem.url)}
                            className={`relative flex cursor-pointer items-center w-full px-2 h-[32px] text-[14px] text-[#3F3F46] font-600 transition-all rounded-md group
                              ${subActive
                                ? "bg-white border border-zinc-200/80"
                                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                              }
                            `}
                          >
                            {subActive && <ActiveMenuItemIndicator />}
                            <div className="flex items-center gap-2.5">
                              {subItem.icon && (
                                <subItem.icon
                                  className={`w-4 h-4 text-[#3F3F46] ${subActive
                                    ? ""
                                    : " group-hover:text-zinc-900"
                                    }`}
                                />
                              )}
                              <span className="text-[#3F3F46]">
                                {subItem.title}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            }

            return (
              <div
                key={item.title}
                onClick={() => {
                  item.onClick ? item.onClick() : navigate(item.url);
                }}
                className={`relative h-[32px] cursor-pointer flex items-center justify-between w-full px-2 text-sm font-600 transition-all rounded-md group
                  ${active
                    ? "bg-white text-[#3F3F46] border border-zinc-200/80"
                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  }
                `}
              >
                {active && <ActiveMenuItemIndicator />}
                <div className="flex items-center gap-2.5">
                  {item.icon && (
                    <item.icon
                      className={`w-4 h-4 text-[#3F3F46] ${active ? "" : " group-hover:text-zinc-900"
                        }`}
                    />
                  )}
                  <span className="text-[#3F3F46]">{item.title}</span>
                </div>
                {item.badge ? (
                  <div className="flex items-center justify-center min-w-[20px] h-[20px] bg-zinc-100 rounded-[4px] border border-zinc-200 px-1.5">
                    <span className="text-[11px] font-600 text-zinc-600">
                      {item.badge}
                    </span>
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        {/* <div className="mt-auto pb-4">
          <NavSecondary items={navSecondaryItems} />
        </div> */}
      </div>

      {/* Footer */}
      {!hideWorkspaces && (
        <div className="p-2 border-t border-zinc-200">
          <NavWorkspace />
        </div>
      )}

      {/* Folder Selection Modal */}
      <FolderSelectionModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSelect={handleFolderSelect}
        title={t("create-specification")}
        description={t("select-folder-description")}
        actionLabel={t("create")}
      />
    </div>
  );
}

const ActiveMenuItemIndicator = () => {
  return (
    <div className="absolute left-[-1px] top-[6px] bottom-[6px] w-[2px] bg-zinc-900 rounded-r-sm" />
  );
};
